<template>
  <div class="carousel-container" :class="`arrow-${arrow.position}`">
    <Carousel
      ref="carouselRef"
      v-model="activeSlideIndex"
      :items-to-show="slidesToShowCount"
      :items-to-scroll="slidesToScrollCount"
      :wrap-around="isInfinite"
      :touch-drag="isSwipeable"
      :mouse-drag="isSwipeable"
      :autoplay="autoplayDuration"
      :pause-autoplay-on-hover="isAutoplayPausedOnHover"
      :breakpoints="breakpoints"
      :snap-align="snapAlign"
      @mouseover="showNavArrows(true)"
      @mouseleave="showNavArrows(false)"
      @slide-end="emit('slide-changed', activeSlideIndex)"
    >
      <slot />

      <template #addons>
        <div
          class="arrow arrow-prev tint-60"
          v-show="navArrowsActiveRef && isPreviousArrowEnabled"
          @click.prevent="slidePrev"
        >
          <UiIcon
            name="chevron"
            :size="arrow.size || '22px'"
            :color-fill="
              isPreviousArrowEnabled
                ? 'var(--color-background-base)'
                : 'var(--color-base-grey-10)'
            "
          />
        </div>
        <div
          class="arrow arrow-next tint-60"
          v-show="navArrowsActiveRef && isNextArrowEnabled"
          @click.prevent="slideNext"
        >
          <UiIcon
            name="chevron"
            :size="arrow.size || '22px'"
            :rotate-deg="180"
            :color-fill="
              isPreviousArrowEnabled
                ? 'var(--color-background-base)'
                : 'var(--color-base-grey-10)'
            "
          />
        </div>
        <Pagination v-if="showDots" @click="emit('pagination-clicked')" />
      </template>
    </Carousel>
  </div>
</template>

<script lang="ts" setup>
import 'vue3-carousel/dist/carousel.css'
import { ref, computed } from 'vue'
import { Carousel, Pagination } from 'vue3-carousel'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'
import {
  AutoplayType,
  NavArrowsDisplayType,
  SlideAlignPosition,
  ArrowPosition,
  type Breakpoints,
  type SlideBorder,
  type Arrow,
  type Vue3CarouselRef,
} from './types'

interface Props {
  activeBorderColor?: string
  arrow?: Arrow
  autoplayType?: AutoplayType
  border?: SlideBorder
  breakpoints?: Breakpoints
  isInfinite?: boolean
  isSwipeable?: boolean
  modelValue?: number
  showDots?: boolean
  slidesToShowCount: number
  slidesToScrollCount: number
  snapAlign?: SlideAlignPosition
}

interface Emits {
  (e: 'arrow-clicked', index: number): void
  (e: 'slide-changed', index: number): void
  (e: 'pagination-clicked'): void
}
const emit = defineEmits<Emits>()

const props = withDefaults(defineProps<Props>(), {
  activeBorderColor: '',
  arrow: () => ({
    size: '22px',
    position: ArrowPosition.OUTSIDE,
    displayType: NavArrowsDisplayType.SHOW,
  }),
  autoplayType: AutoplayType.NO_AUTOPLAY,
  border: () => ({
    color: 'var(--color-base-white)',
    activeColor: 'var(--color-base-black)',
    radius: 'var(--size-radius-none)',
    size: '1px',
    activeSize: '1px',
  }),
  breakpoints: undefined,
  isInfinite: false,
  isSwipeable: true,
  modelValue: 0,
  showDots: true,
  snapAlign: SlideAlignPosition.START,
})

const carouselRef = ref<Vue3CarouselRef | null>(null)

const activeSlideIndex = defineModel<number>({
  default: 0,
})

// fixed props
const slideGap: string = props.slidesToShowCount === 1 ? '0px' : '4px'

/**
 * navigation arrows enablement and functions
 */
const initialArrowActive: boolean =
  props.arrow.displayType === NavArrowsDisplayType.SHOW
const navArrowsActiveRef = ref(initialArrowActive)
const showNavArrows = (onHover: boolean) =>
  (navArrowsActiveRef.value =
    props.arrow.displayType === NavArrowsDisplayType.HIDE
      ? false
      : props.arrow.displayType === NavArrowsDisplayType.SHOW
      ? true
      : onHover)

const autoplayDuration = computed<number>(() => {
  return props.autoplayType !== AutoplayType.NO_AUTOPLAY ? 5000 : 0
})

const isAutoplayPausedOnHover = computed<boolean>(() => {
  return props.autoplayType === AutoplayType.PAUSE_AUTOPLAY_ON_HOVER
})

/**
 * navigation arrows enablement and functions
 */
const maxSlideIndex = computed<number>(
  () => carouselRef.value?.data.maxSlide.value || 0
)

const isLastSlide = computed<boolean>(
  () => activeSlideIndex.value >= maxSlideIndex.value
)

const isPreviousArrowEnabled = computed<boolean>(() => {
  if (props.autoplayType !== AutoplayType.NO_AUTOPLAY) return true
  if (props.isInfinite) return true

  return activeSlideIndex.value > 0
})

const isNextArrowEnabled = computed<boolean>(() => {
  if (props.autoplayType !== AutoplayType.NO_AUTOPLAY) return true
  if (props.isInfinite) return true

  return !isLastSlide.value
})

const slidePrev = (): void => {
  // 0.5 make the index value be integer in case slidesToShowCount is half integer
  const prevIndex = Math.floor(
    activeSlideIndex.value -
      (carouselRef?.value?.data?.config?.itemsToScroll ||
        props.slidesToScrollCount) +
      0.5
  )

  carouselRef.value?.slideTo(prevIndex)

  const currentIndex = carouselRef.value?.data.currentSlide.value ?? 0
  emit('arrow-clicked', currentIndex)
}

const slideNext = (): void => {
  carouselRef.value?.next()

  const currentIndex = carouselRef.value?.data.currentSlide.value ?? 0
  emit('arrow-clicked', currentIndex)
}
</script>

<style lang="scss" scoped>
.carousel-container {
  margin: 0 auto;

  &.arrow-outside {
    $arrowSize: calc(-40px - 16px);
    width: calc(100% + #{$arrowSize} * 2);

    .arrow-prev {
      left: $arrowSize;
    }

    .arrow-next {
      right: $arrowSize;
    }
  }

  &.arrow-within {
    width: 100%;

    .arrow-prev {
      left: 16px;
    }

    .arrow-next {
      right: 16px;
    }
  }

  .arrow {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    position: absolute;
    top: calc(50% - 20px);
    border-radius: 50%;
  }

  &.thumbnails :deep(li) {
    cursor: pointer;
  }

  & + .thumbnails {
    :deep(.carousel__slide) > * {
      border: v-bind('props.border.size') solid v-bind('props.border.color');
      border-radius: v-bind('props.border.radius');
    }

    :deep(.carousel__slide--active) > * {
      border: v-bind('props.border.activeSize') solid
        v-bind('props.border.activeColor');
    }
  }

  &.gallery :deep(.carousel__track) {
    transition: none !important;
  }
}
.carousel {
  // modify vue3-carousel built-in styles
  :deep(.carousel__slide) {
    padding-inline: v-bind('slideGap');

    & > * {
      width: 100%;
    }
  }

  :deep(.iframe-container) {
    position: relative;
    height: 0;
    margin: auto;
    padding: 0% 0% 56.25%; /* 16:9 ratio */
    overflow: hidden;

    iframe {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  :deep(.carousel__pagination) {
    --vc-pgn-height: 8px;
    --vc-pgn-width: 8px;
    --vc-pgn-border-radius: 100%;
    padding-inline-start: 0px;
    margin-top: 16px;
  }

  // to prevent click while dragging
  &.is-dragging {
    :deep(.carousel__track) {
      pointer-events: none;
    }
  }
}
</style>
